/**
 * useChatFileUpload
 *
 * Hook for managing file uploads in mission chat.
 * Uploads files to a BYOH host via hostsApi.uploadFileToHost() and
 * tracks per-file upload state (uploading, complete, error).
 *
 * Usage:
 *   const { pendingFiles, isUploading, addFiles, removeFile, retryFile, getAttachments, reset } =
 *     useChatFileUpload({ hostId: mission?.hostId })
 */

import { useState, useCallback, useRef } from 'react'
import type { UserMessageAttachment } from '@agion/mission-stream'
import { hostsApi } from '../services/api'

export interface PendingFile {
  id: string
  filename: string
  size: number
  contentType: string
  status: 'uploading' | 'complete' | 'error'
  progress: number
  hostFileId?: string
  /** Destination captured for this upload attempt, retained across host changes. */
  hostId?: string
  error?: string
}

interface UseChatFileUploadOptions {
  hostId: string | undefined
  maxSizeBytes?: number
}

interface UseChatFileUploadReturn {
  pendingFiles: PendingFile[]
  isUploading: boolean
  addFiles: (files: File[]) => string[]
  removeFile: (id: string) => void
  retryFile: (id: string) => void
  clearCompleted: () => void
  getAttachments: () => UserMessageAttachment[]
  reset: () => void
  /**
   * Stages files that are already on the host again, as uploaded: the files
   * of an answer that failed or was refused (phase 4, agion/Agion#2327). A
   * file already staged is not added twice.
   */
  restore: (attachments: UserMessageAttachment[]) => void
}

const DEFAULT_MAX_SIZE = 25 * 1024 * 1024 // 25MB

export function useChatFileUpload({
  hostId,
  maxSizeBytes = DEFAULT_MAX_SIZE,
}: UseChatFileUploadOptions): UseChatFileUploadReturn {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  // Keep original File references for retry
  const fileMapRef = useRef<Map<string, File>>(new Map())

  const isUploading = pendingFiles.some((f) => f.status === 'uploading')

  const uploadFile = useCallback(
    async (id: string, file: File) => {
      if (!hostId) {
        setPendingFiles((prev) =>
          prev.map((f) =>
            f.id === id
              ? { ...f, status: 'error', error: 'No host available' }
              : f,
          ),
        )
        return
      }

      if (file.size > maxSizeBytes) {
        setPendingFiles((prev) =>
          prev.map((f) =>
            f.id === id
              ? { ...f, status: 'error', error: 'File exceeds 25 MB limit' }
              : f,
          ),
        )
        return
      }

      const uploadHostId = hostId
      setPendingFiles((prev) => prev.map((f) => f.id === id ? { ...f, hostId: uploadHostId } : f))
      try {
        const result = await hostsApi.uploadFileToHost(uploadHostId, file, 'chat_attachment')
        setPendingFiles((prev) =>
          prev.map((f) =>
            f.id === id
              ? { ...f, status: 'complete', progress: 100, hostFileId: result.id }
              : f,
          ),
        )
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed'
        setPendingFiles((prev) =>
          prev.map((f) =>
            f.id === id ? { ...f, status: 'error', error: message } : f,
          ),
        )
      }
    },
    [hostId, maxSizeBytes],
  )

  const addFiles = useCallback(
    (files: File[]) => {
      const newPendingFiles: PendingFile[] = []

      for (const file of files) {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
        fileMapRef.current.set(id, file)

        if (!hostId) {
          newPendingFiles.push({
            id,
            filename: file.name,
            size: file.size,
            contentType: file.type || 'application/octet-stream',
            status: 'error',
            progress: 0,
            error: 'No host available',
          })
          continue
        }

        if (file.size > maxSizeBytes) {
          newPendingFiles.push({
            id,
            filename: file.name,
            size: file.size,
            contentType: file.type || 'application/octet-stream',
            status: 'error',
            progress: 0,
            error: 'File exceeds 25 MB limit',
          })
          continue
        }

        newPendingFiles.push({
          id,
          filename: file.name,
          size: file.size,
          contentType: file.type || 'application/octet-stream',
          status: 'uploading',
          progress: 0,
        })
      }

      setPendingFiles((prev) => [...prev, ...newPendingFiles])

      // Kick off uploads for files that passed validation
      for (const pending of newPendingFiles) {
        if (pending.status === 'uploading') {
          const file = fileMapRef.current.get(pending.id)
          if (file) {
            uploadFile(pending.id, file)
          }
        }
      }
      return newPendingFiles.map((file) => file.id)
    },
    [hostId, maxSizeBytes, uploadFile],
  )

  const removeFile = useCallback((id: string) => {
    fileMapRef.current.delete(id)
    setPendingFiles((prev) => prev.filter((f) => f.id !== id))
  }, [])

  const retryFile = useCallback(
    (id: string) => {
      const file = fileMapRef.current.get(id)
      if (!file) return

      setPendingFiles((prev) =>
        prev.map((f) =>
          f.id === id
            ? { ...f, status: 'uploading', progress: 0, error: undefined, hostFileId: undefined }
            : f,
        ),
      )

      uploadFile(id, file)
    },
    [uploadFile],
  )

  const clearCompleted = useCallback(() => {
    setPendingFiles((prev) => {
      const remaining = prev.filter((f) => f.status !== 'complete')
      // Clean up file references for removed files
      for (const f of prev) {
        if (f.status === 'complete') {
          fileMapRef.current.delete(f.id)
        }
      }
      return remaining
    })
  }, [])

  const getAttachments = useCallback((): UserMessageAttachment[] => {
    return pendingFiles
      .filter((f) => f.status === 'complete' && f.hostFileId)
      .map((f) => ({
        host_file_id: f.hostFileId!,
        ...(f.hostId !== undefined ? { host_id: f.hostId } : {}),
        filename: f.filename,
        content_type: f.contentType,
        size: f.size,
      }))
  }, [pendingFiles])

  const reset = useCallback(() => {
    fileMapRef.current.clear()
    setPendingFiles([])
  }, [])

  const restore = useCallback((attachments: UserMessageAttachment[]) => {
    setPendingFiles((prev) => {
      const staged = new Set(prev.map((f) => f.hostFileId).filter(Boolean))
      const restored = attachments
        .filter((a) => !staged.has(a.host_file_id))
        .map((a): PendingFile => ({
          id: `restored-${a.host_file_id}`,
          filename: a.filename,
          size: a.size,
          contentType: a.content_type,
          status: 'complete',
          progress: 100,
          hostFileId: a.host_file_id,
          hostId: a.host_id,
        }))
      return restored.length ? [...prev, ...restored] : prev
    })
  }, [])

  return {
    pendingFiles,
    isUploading,
    addFiles,
    removeFile,
    retryFile,
    clearCompleted,
    getAttachments,
    reset,
    restore,
  }
}
